import { API_BASE_URL } from '../config/api';

// Cache for user permissions
let userPermissionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Permission mapping for navigation items
export const NAVIGATION_PERMISSIONS = {
  // Data Management
  'customers': 'customers.read',
  'class-of-trade': 'customers.manage_cot',
  'products': 'products.read',
  'contracts': 'contracts.read',
  'gpos': 'customers.read', // GPOs are part of customer management
  'sales-data': 'reports.view',
  
  // Processing
  'chargebacks': 'chargebacks.read',
  'upload': 'edi.upload',
  'edi': 'edi.read',
  'batches': 'batches.read',
  
  // Reports
  'reports': 'reports.view',
  
  // AMP
  'amp': 'amp.view',
  
  // Admin
  'admin': 'users.read', // Admin access requires user management permission
  'user-management': 'users.read',
  'role-management': 'users.read',
  'user-permissions': 'users.manage_permissions',
  'test-data': 'system.settings',
  'system-settings': 'system.settings'
};

// Fetch user permissions from API
export const fetchUserPermissions = async () => {
  const token = localStorage.getItem('token');
  if (!token) return [];

  try {
    const response = await fetch(`${API_BASE_URL}/api/user-permissions/users/me/permissions`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch permissions');
    }
    
    const data = await response.json();
    return data.data?.permissions || {};
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    return {};
  }
};

// Get user permissions (with caching)
export const getUserPermissions = async () => {
  const now = Date.now();
  
  // Return cached permissions if still valid
  if (userPermissionsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return userPermissionsCache;
  }
  
  // Fetch fresh permissions
  const permissions = await fetchUserPermissions();
  userPermissionsCache = permissions;
  cacheTimestamp = now;
  
  return permissions;
};

// Check if user has a specific permission
export const hasPermission = async (permissionCode) => {
  const permissions = await getUserPermissions();
  
  // Flatten permissions from all categories
  const allPermissions = Object.values(permissions).flat();
  
  // Find the permission
  const permission = allPermissions.find(p => p.code === permissionCode);
  
  return permission ? permission.granted : false;
};

// Check if user has any of the specified permissions
export const hasAnyPermission = async (permissionCodes) => {
  const permissions = await getUserPermissions();
  const allPermissions = Object.values(permissions).flat();
  
  return permissionCodes.some(code => {
    const permission = allPermissions.find(p => p.code === code);
    return permission ? permission.granted : false;
  });
};

// Check if user can access a navigation item
export const canAccessNavigationItem = async (itemKey) => {
  const requiredPermission = NAVIGATION_PERMISSIONS[itemKey];
  if (!requiredPermission) return true; // No permission required
  
  return await hasPermission(requiredPermission);
};

// Clear permissions cache (useful after permission changes)
export const clearPermissionsCache = () => {
  userPermissionsCache = null;
  cacheTimestamp = null;
};

// Get permission summary for debugging
export const getPermissionSummary = async () => {
  const permissions = await getUserPermissions();
  const allPermissions = Object.values(permissions).flat();
  
  return {
    total: allPermissions.length,
    granted: allPermissions.filter(p => p.granted).length,
    revoked: allPermissions.filter(p => !p.granted).length,
    categories: Object.keys(permissions)
  };
};
